import type { ScreenerColumn } from "epsm-api-types/models";
import type React from "react";
import { createContext, type ReactNode, useContext, useState } from "react";
import { DEFAULT_SCORER_COLUMNS } from "@/components/widgets/Screener/default";
import type { ScorerColumn } from "@/components/widgets/Screener/type";

export type ViewState<T> = {
	[key: string]: T;
};

export interface WidgetViewContextProps {
	isShowDescription: ViewState<boolean>;
	setIsShowDescription: (value: ViewState<boolean>) => void;
	visibleColumns: ViewState<ScreenerColumn[]>;
	setVisibleColumns: React.Dispatch<
		React.SetStateAction<ViewState<ScreenerColumn[]>>
	>;
	scorerColumns: ScorerColumn[];
	setScorerColumns: React.Dispatch<React.SetStateAction<ScorerColumn[]>>;
	selectedColumn: ViewState<ScreenerColumn | null>;
	setSelectedColumn: (value: ViewState<ScreenerColumn | null>) => void;
	INITIAL_GROUP: string;
	selectedGroup: ViewState<string>;
	setSelectedGroup: (value: ViewState<string>) => void;
	isRelative: boolean;
	setIsRelative: React.Dispatch<React.SetStateAction<boolean>>;
}

const WidgetViewContext = createContext<WidgetViewContextProps | undefined>(
	undefined,
);

export const WidgetViewProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [isShowDescription, setIsShowDescription] = useState<
		ViewState<boolean>
	>({
		Screener: false,
		Monitor: false,
	});
	const [visibleColumns, setVisibleColumns] = useState<
		ViewState<ScreenerColumn[]>
	>({
		Screener: [],
		Monitor: [],
	});
	const [scorerColumns, setScorerColumns] = useState<ScreenerColumn[]>(
		DEFAULT_SCORER_COLUMNS as ScreenerColumn[],
	);
	const [selectedColumn, setSelectedColumn] = useState<
		ViewState<ScreenerColumn | null>
	>({
		Screener: null,
		Monitor: null,
	});
	const INITIAL_GROUP = "All";
	const [selectedGroup, setSelectedGroup] = useState<ViewState<string>>({
		Screener: INITIAL_GROUP,
		Monitor: INITIAL_GROUP,
	});

	const [isRelative, setIsRelative] = useState(false);

	return (
		<WidgetViewContext.Provider
			value={{
				isShowDescription,
				setIsShowDescription,
				visibleColumns,
				setVisibleColumns,
				selectedColumn,
				setSelectedColumn,
				INITIAL_GROUP,
				selectedGroup,
				setSelectedGroup,
				isRelative,
				setIsRelative,
				scorerColumns,
				setScorerColumns,
			}}
		>
			{children}
		</WidgetViewContext.Provider>
	);
};

export const useWidgetView = () => {
	const context = useContext(WidgetViewContext);

	if (!context) {
		throw new Error("useWidgetView must be used within a WidgetViewProvider");
	}
	return context;
};
